import React, {useCallback, useEffect, useState} from 'react';
import {ReactFlowProvider} from 'react-flow-renderer';

import {LayoutFlow} from './LayoutFlow';
import {Artifact, Dag, Run} from './utils';

type ArtifactFlowDagProps = {
  dag: Dag;
  artifactID: string;
  filterGeneratedArtifacts: boolean;
  clusterSize?: number;
  directLineage: boolean;
};

export const ArtifactFlowDag = (props: ArtifactFlowDagProps) => {
  const [dag, setDag] = useState(props.dag);
  const {filterGeneratedArtifacts, clusterSize, directLineage, artifactID} =
    props;
  useEffect(() => {
    setDag(props.dag);
    // Graph reloads too often if added in the dependencies
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterGeneratedArtifacts, clusterSize, directLineage]);

  const [tempKey, setTempKey] = useState('');
  const [expandArtifact, setExpandArtifact] = useState('');
  const [expandRun, setExpandRun] = useState('');

  const updateDag = useCallback(
    (newDag: Dag, expandedArtifact: string, expandedRun: string) => {
      const tempDag = {...dag};
      for (const [k, v] of Object.entries(newDag.artifacts)) {
        tempDag.artifacts[k] = v;
      }
      for (const [k, v] of Object.entries(newDag.runs)) {
        tempDag.runs[k] = v;
      }
      for (const [k, v] of Object.entries(newDag.clusters)) {
        tempDag.clusters[k] = v;
      }
      for (const [k, v] of Object.entries(newDag.edges)) {
        tempDag.edges[k] = v;
      }

      Object.entries(tempDag.clusters).forEach(([cID, cNode]) => {
        const tempArtifactNodes =
          cNode?.artifactNodes?.filter(
            (artNode: Artifact) =>
              // eslint-disable-next-line no-prototype-builtins
              !tempDag.artifacts.hasOwnProperty(artNode.artifactNodeID)
          ) ?? [];
        const tempRunNodes =
          cNode?.runNodes?.filter(
            // eslint-disable-next-line no-prototype-builtins
            (runNode: Run) => !tempDag.runs.hasOwnProperty(runNode.runNodeID)
          ) ?? [];

        const tempEdgeNodes = cNode?.clusterEdges ?? [];

        tempDag.clusters[cID] = {
          ...tempDag.clusters[cID],
          artifactNodes: tempArtifactNodes,
          runNodes: tempRunNodes,
          clusterEdges: tempEdgeNodes,
        };
      });
      setExpandArtifact(expandedArtifact);
      setExpandRun(expandedRun);
      setDag(tempDag);
      setTempKey(Math.random().toString(36));
    },
    [dag]
  );

  return (
    <ReactFlowProvider>
      <LayoutFlow
        artifactID={artifactID}
        dag={dag}
        expandedArtifact={expandArtifact}
        expandedRun={expandRun}
        updateDag={updateDag}
        filterGeneratedArtifacts={filterGeneratedArtifacts}
        clusterSize={clusterSize}
        directLineage={directLineage}
        key={tempKey}
      />
    </ReactFlowProvider>
  );
};

export default ArtifactFlowDag;
